import { ExportIcon } from '@features/ui/icons';
import { PaDailyPlHitting30PercentOfTargetTable } from '@features/admin/reports';
import { useState } from 'react';
import { DateValueType, downloadFile } from '@lib/utils';
import {
  TPaDailyPlHitting30PercentOfTargetTable,
  TReportOneDayProfitData,
} from '@src/types/reports';
import { getOneDayProfit, exportOneDayProfit } from '@src/services/reports';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

export const PaDailyPlHitting30PercentOfTarget = () => {
  const DEFAULT_PAGE_SIZE = 50;

  const [reportTitle] = useState('PA Traders with excessive Daily Profit');
  const [reportDescription] = useState(
    'Filter and view PA accounts with single daily profit hitting a significant percentage of total profit'
  );

  const [accountStatus, setAccountStatus] = useState('');
  const [profitPercent, setProfitPercent] = useState('');
  const [minTradingDays, setMinTradingDays] = useState('');
  const [startDate, setStartDate] = useState<DateValueType>(null);
  const [data, setData] = useState<TPaDailyPlHitting30PercentOfTargetTable[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Map API response to table data structure
  const mapApiDataToTableData = (
    apiData: TReportOneDayProfitData[]
  ): TPaDailyPlHitting30PercentOfTargetTable[] => {
    return apiData.map((item) => ({
      id: item.account_id.toString(),
      userName: item.user_name,
      userId: item.user_id.toString(),
      accountNo: item.account_id.toString(),
      accountDes: item.account_name,
      package: item.package_name,
      status: item.status,
      balance: item.balance ? parseFloat(item.balance) : undefined,
      notes: item.notes,
      autoLiquidatedOn: item.auto_liquidated_on,
      totalProfit: parseFloat(item.total_profit),
      maxAllowedDailyProfit: parseFloat(item.max_day_profit_allowed),
      highestDailyProfit: parseFloat(item.best_day_profit),
      highestDailyProfitPercent: parseFloat(item.profit_percent),
      tradingDays: item.account_trading_days,
      fullName: item.full_name,
      lastPayout: item.last_payout_date,
      accountPayoutCount: item.account_payouts,
      accountPayoutTotal: parseFloat(item.account_payout_total),
      customerPayoutCount: item.user_payouts,
      customerPayoutTotal: parseFloat(item.user_payout_total),
    }));
  };

  const buildParams = (page = currentPage, perPage = pageSize) => ({
    start_date: startDate?.startDate
      ? format(startDate.startDate, 'yyyy-MM-dd')
      : undefined,
    live_accounts: accountStatus === 'live_only' ? true : undefined,
    profit_percent: profitPercent ? parseFloat(profitPercent) : undefined,
    min_trading_days: minTradingDays ? parseInt(minTradingDays) : undefined,
    page,
    per_page: perPage,
  });

  const fetchReport = async (page = 1, perPage = pageSize) => {
    setIsLoading(true);
    try {
      const res = await getOneDayProfit(buildParams(page, perPage));
      const mappedData = mapApiDataToTableData(res.data);

      setData(mappedData);
      setCurrentPage(res.page ?? page);
      setPageSize(res.per_page ?? perPage);
      setTotalCount(res.total_count ?? 0);
      setTotalPages(res.total_pages ?? 1);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
      console.error('Error fetching one-day profit data:', error);
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetData = async () => {
    await fetchReport(1, pageSize);
  };

  const handlePageChange = async (page: number) => {
    await fetchReport(page, pageSize);
  };

  const handlePageSizeChange = async (nextSize: number) => {
    await fetchReport(1, nextSize);
  };

  const handleExport = async () => {
    try {
      const res = await exportOneDayProfit(buildParams());

      downloadFile(res.data, 'pa-daily-pl-hitting-30-percent');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report');
      console.error('Error exporting one-day profit data:', error);
    }
  };

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <div className={'text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3'}>
          <h1 className={'text-[23px]'}>{reportTitle}</h1>
          <p className={'max-w-[482px]'}>{reportDescription}</p>
        </div>
        <button
          className={
            'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit inline-flex items-center justify-center gap-2 bg-transparent px-4 text-sm font-medium text-inherit transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50'
          }
          onClick={handleExport}
        >
          <span>Export</span>
          <ExportIcon />
        </button>
      </div>
      <PaDailyPlHitting30PercentOfTargetTable
        accountStatus={accountStatus}
        currentPage={currentPage}
        data={data}
        isLoading={isLoading}
        minTradingDays={minTradingDays}
        pageSize={pageSize}
        profitPercent={profitPercent}
        setAccountStatus={setAccountStatus}
        setMinTradingDays={setMinTradingDays}
        setProfitPercent={setProfitPercent}
        setStartDate={setStartDate}
        startDate={startDate}
        totalCount={totalCount}
        totalPages={totalPages}
        onGetData={handleGetData}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};
